import {
  ActionMenuItem,
  Caption,
  ControlledNumberField,
  ControlledSelect,
  FieldsetCard,
  useFormContainerContext,
} from '@if/mui'
import { Grid } from '@mui/material'

import { EPaymentPlan } from '@if/core/types'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import {
  PersonalLoanCalculatorConstants,
  PersonalLoanCalculatorPaymentOption,
  PersonalLoanCalculatorTermOption,
} from '@src/types/Constants'
import { PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const { MIN_AMOUNT } = PersonalLoanCalculatorConstants

interface LoanDetailsFormProps {
  disabled?: boolean
  paymentData: PersonalLoanCalculatorPaymentOption[]
  termData: PersonalLoanCalculatorTermOption[]
  /** The terms come from a second request, after the form has values to send. */
  isLoadingTerms?: boolean
  /** Category-based ceiling (higher for Medicard merchants) — see CalculatorForm. */
  maxLoanAmount: number
}

export const LoanDetailsForm = ({
  disabled = false,
  paymentData,
  termData,
  isLoadingTerms = false,
  maxLoanAmount,
}: LoanDetailsFormProps) => {
  const { t } = useTranslation()
  const {
    methods: { control },
  } = useFormContainerContext<PersonalLoanCalculatorSchemaType>()

  const paymentOptions = useMemo(
    () =>
      paymentData.map((option) => (
        <ActionMenuItem key={option.value} value={option.value}>
          {option.label.includes(EPaymentPlan.RegularDailyInterests)
            ? t(`EPaymentPlan.${EPaymentPlan.RegularDailyInterests}`, { ns: 'enum' })
            : option.label}
        </ActionMenuItem>
      )),
    [paymentData, t],
  )

  // Only "no terms" once the request has answered — otherwise the loading gap would
  // flash an error claiming none exist.
  // A promotional plan supplies its own terms, so a non-empty list means loaded
  // regardless of what the terms request is doing.
  const hasNoTerms = !isLoadingTerms && termData.length === 0
  const isWaitingForTerms = isLoadingTerms && termData.length === 0
  const termHelperKey = isWaitingForTerms
    ? 'calculator.loanTermLoading'
    : hasNoTerms
      ? 'calculator.loanTermUnavailable'
      : 'calculator.loanTermHelper'

  const termOptions = useMemo(
    () =>
      termData.map((option) => (
        <ActionMenuItem key={option.value} value={option.value}>
          {option.label}
        </ActionMenuItem>
      )),
    [termData],
  )

  return (
    <FieldsetCard title={t('calculator.loanDetails')}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <ControlledNumberField
            control={control}
            name="amountRequested"
            label={t('calculator.requestedAmountLabel')}
            fullWidth
            disabled={disabled}
            isMoney
          />
          <Caption sx={{ display: 'block', mt: 0.5 }}>
            {t('calculator.requestedAmountHelper', {
              min: formatCurrencyNoDecimals(MIN_AMOUNT),
              max: formatCurrencyNoDecimals(maxLoanAmount),
            })}
          </Caption>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <ControlledSelect
            control={control}
            name="paymentPlanId"
            label={t('calculator.paymentPlanLabel')}
            disableEmptyOption
            fullWidth
            options={paymentOptions}
            disabled={disabled || paymentData.length === 0}
          />
          <Caption sx={{ display: 'block', mt: 0.5 }}>{t('calculator.paymentPlanHelper')}</Caption>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          {/* Stays mounted (no skeleton swap) while the term list loads, to avoid a layout shift. */}
          <ControlledSelect
            control={control}
            name="term"
            label={t('calculator.loanTermLabel')}
            disableEmptyOption
            fullWidth
            options={termOptions}
            disabled={disabled || isWaitingForTerms || hasNoTerms}
          />
          <Caption sx={{ display: 'block', mt: 0.5 }} color={hasNoTerms ? 'error' : undefined}>
            {t(termHelperKey)}
          </Caption>
        </Grid>
      </Grid>
    </FieldsetCard>
  )
}
